import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import { abs, applyTheme } from "../lib/theme";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { GraduationCap, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const PERSONAS = [
  { label: "Login as Super Admin", email: "forevershah2000@gmail.com", password: "Admin@123" },
  { label: "Login as Admissions Dean", email: "dean@edupulse.in", password: "Dean@123" },
  { label: "Login as Counselor", email: "counselor@edupulse.in", password: "Counselor@123" },
  { label: "Login as Publisher Partner", email: "publisher@edupulse.in", password: "Publisher@123" },
  { label: "Login as Finance Officer", email: "finance@edupulse.in", password: "Finance@123" },
];

const DEFAULT_HERO = {
  brand_name: "EduPulse CRM",
  background_image_url:
    "https://images.unsplash.com/photo-1583373834259-46cc92173cb7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxoaWdoZXIlMjBlZHVjYXRpb24lMjB1bml2ZXJzaXR5JTIwY2FtcHVzJTIwYnVpbGRpbmclMjBhcmNoaXRlY3R1cmFsfGVufDB8fHx8MTc4ODI4NDE3MXww&ixlib=rb-4.1.0&q=85",
  headline: "Every inquiry.\nEvery attribution.\nOne admissions engine.",
  bullets: [
    "Real-time publisher funnel analytics across 12+ states",
    "Deduplicated multi-instance lead engine with tiered attribution",
    "Quota-enforced webhooks with token-signed ingestion",
  ],
};

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [inst, setInst] = useState(null);
  const [logoFailed, setLogoFailed] = useState(false);

  useEffect(() => {
    api.get("/tenant/branding")
      .then((r) => {
        setInst(r.data);
        setLogoFailed(false);
        applyTheme({ branding: r.data.branding, title: `${r.data.name} — Sign in` });
      })
      .catch(() => {});
  }, []);

  const doLogin = async (e, p) => {
    setBusy(true);
    try {
      await login(e, p);
      toast.success("Welcome back");
      navigate("/analytics");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const lh = inst?.branding?.login_hero || {};
  const hero = {
    brand_name: lh.brand_name || inst?.name || DEFAULT_HERO.brand_name,
    logo_url: abs(lh.logo_url || inst?.branding?.logo_url || ""),
    background_image_url: abs(lh.background_image_url) || DEFAULT_HERO.background_image_url,
    headline: lh.headline || DEFAULT_HERO.headline,
    bullets: (lh.bullets && lh.bullets.length ? lh.bullets : DEFAULT_HERO.bullets),
  };
  const headlineLines = hero.headline.split("\n");
  const showLogo = Boolean(hero.logo_url) && !logoFailed;

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      <div
        className="hidden lg:flex w-1/2 relative bg-slate-900 text-white flex-col justify-between p-12"
        data-testid="login-hero"
        style={{ backgroundImage: `url(${hero.background_image_url})`, backgroundSize: "cover", backgroundPosition: "center" }}
      >
        <div className="absolute inset-0 bg-slate-900/75" />
        <div className="relative flex items-center gap-3">
          {showLogo ? (
            <img src={hero.logo_url} alt={hero.brand_name} data-testid="login-hero-logo"
              className="w-10 h-10 rounded-lg object-contain bg-white/10 backdrop-blur p-1"
              onError={() => setLogoFailed(true)}
              onLoad={(e) => {
                // a blank / 1×1 placeholder counts as "no logo"
                if (e.currentTarget.naturalWidth <= 2 || e.currentTarget.naturalHeight <= 2) setLogoFailed(true);
              }} />
          ) : (
            <div className="w-10 h-10 rounded-lg bg-white/10 backdrop-blur flex items-center justify-center overflow-hidden"
              data-testid="login-hero-logo-fallback">
              <img src="/logo.png" alt="EduPulse" className="w-8 h-8 object-contain"
                onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextElementSibling.style.display = "block"; }} />
              <GraduationCap className="w-6 h-6 text-white" style={{ display: "none" }} />
            </div>
          )}
          <span className="font-display text-xl font-bold" data-testid="login-hero-brand">{hero.brand_name}</span>
        </div>
        <div className="relative">
          <h1 className="font-display text-4xl font-bold leading-tight mb-6" data-testid="login-hero-headline">
            {headlineLines.map((line, i) => (
              <span key={i}>{line}{i < headlineLines.length - 1 && <br />}</span>
            ))}
          </h1>
          <div className="space-y-4" data-testid="login-hero-bullets">
            {hero.bullets.map((text, i) => (
              <div key={i} className="flex items-start gap-3 text-slate-300">
                <CheckCircle2 className="w-5 h-5 text-blue-400 shrink-0 mt-0.5" />
                <span className="text-sm">{text}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="relative text-xs text-slate-400">{inst?.name || ""}</div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8 bg-[#F8FAFC]">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8">
            <h2 className="font-display text-2xl font-bold text-slate-900 mb-1">Sign in</h2>
            <p className="text-sm text-slate-500 mb-6">Access the admissions command center</p>
            <form
              onSubmit={(e) => { e.preventDefault(); doLogin(email, password); }}
              className="space-y-4"
              data-testid="login-form"
            >
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email" data-testid="login-email-input" type="email" required
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@university.edu" className="mt-1.5"
                />
              </div>
              <div>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password" data-testid="login-password-input" type="password" required
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••" className="mt-1.5"
                />
              </div>
              <Button data-testid="login-submit-button" type="submit" disabled={busy}
                className="w-full bg-brand">
                {busy ? "Signing in…" : "Sign in"}
              </Button>
            </form>
          </div>

          <div className="mt-6">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3 text-center">
              Quick persona access
            </div>
            <div className="grid grid-cols-1 gap-2">
              {PERSONAS.map((p) => (
                <button
                  key={p.email}
                  data-testid={`persona-${p.email}`}
                  disabled={busy}
                  onClick={() => doLogin(p.email, p.password)}
                  className="text-left text-sm px-4 py-2.5 rounded-lg border border-slate-200 bg-white hover:border-blue-400 hover:bg-blue-50/50 transition-colors text-slate-700 disabled:opacity-50"
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
